import {
  Alert,
  AlertTitle,
  Button,
  Card,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SvgIcon,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  RiArrowLeftRightLine,
  RiCornerUpRightDoubleLine,
  RiDeleteBinLine,
  RiForbid2Line,
} from '@remixicon/react';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import CustomChip from '../../../components/CustomChip';
import InputHeader from '../../../components/InputHeader';
import { useLanguage } from '../../../contexts/language';
import useForm from '../../../hooks/useForm';
import { useNavigationGuardBlocker } from '../../../hooks/useNavigationGuard';
import { PortSelectItem } from '../../../types/port';
import { RouteDay, SimpleNewRoute } from '../../../types/route';
import {
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_DATA_TIME_FORMAT,
  DAYJS_VISUALIZATION_DATE_FORMAT,
  getNullableDayjsDifferenceChecker,
} from '../../../utils/date';
import { renderPortSelectItemLabel } from '../../../utils/port';
import { ProcessedRoute } from './types';
import { RouteStatus } from '../../../types/core';
import { BADGE_CLASSNAMES } from '../../../utils/styles';
import getMaterialInputsFormHelper from '../../../utils/getMaterialInputsFormHelper';

export interface RouteElementProps {
  route: ProcessedRoute | null;
  copiedRoute?: ProcessedRoute | null;
  existingRoutes: ProcessedRoute[];
  isLoading: boolean;
  contractDateFrom: Dayjs;
  contractDateTo: Dayjs;
  defaultDeparturePort: PortSelectItem | null;
  defaultArrivalPort: PortSelectItem | null;
  allowedPorts: PortSelectItem[];
  onDelete: (hasPendingChanges: boolean) => void;
  onRescind?: () => void;
  onSave?: (routeValues: SimpleNewRoute) => void;
  onCopyToNextYear?: () => void;
  areActionsDisabled?: boolean;
}

type RouteFormValues = {
  portOfDepartureId: string;
  portOfArrivalId: string;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  repeatedOnDays: RouteDay[];
  departureTime: Dayjs | null;
};

const RouteElement = ({
  route,
  existingRoutes,
  contractDateFrom: contractDateFromProp,
  contractDateTo: contractDateToProp,
  allowedPorts,
  defaultDeparturePort,
  defaultArrivalPort,
  isLoading,
  onDelete,
  onRescind,
  onSave,
  onCopyToNextYear,
  copiedRoute,
  areActionsDisabled = false,
}: RouteElementProps) => {
  const { t } = useLanguage();

  // The next dependency for the memo ensures that if the day changes while the user is working with the form
  // It will be reflected in the validations (edge case)
  const currentDate = new Date().getDate();
  const { contractDateTo, contractDateFrom } = useMemo(() => {
    const contractDateFrom = dayjs(contractDateFromProp);
    const contractDateTo = dayjs(contractDateToProp);
    return {
      contractDateFrom: contractDateFrom.isValid() ? contractDateFrom : null,
      contractDateTo: contractDateTo.isValid() ? contractDateTo : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractDateToProp, contractDateFromProp, currentDate]);

  const canCopyToNextYear =
    route &&
    !route.dateFrom.add(1, 'year').isAfter(contractDateTo, 'day') &&
    !route.dateTo.add(1, 'year').isAfter(contractDateTo, 'day');

  const { departureSelectablePorts, arrivalSelectablePorts, invalidPortIds } =
    useMemo<{
      departureSelectablePorts: PortSelectItem[];
      arrivalSelectablePorts: PortSelectItem[];
      invalidPortIds: Set<string>;
    }>(() => {
      const invalidPortIds = new Set<string>();
      const baseSelectablePorts: PortSelectItem[] = [...allowedPorts];

      allowedPorts.forEach(port => {
        if (port.issue) {
          invalidPortIds.add(port.value);
        }
      });

      if (!route) {
        return {
          arrivalSelectablePorts: baseSelectablePorts,
          departureSelectablePorts: baseSelectablePorts,
          invalidPortIds,
        };
      }

      const departureSelectablePorts = [...baseSelectablePorts];
      if (
        !departureSelectablePorts.find(
          port => port.value === route.portOfDeparture.value,
        )
      ) {
        departureSelectablePorts.push(route.portOfDeparture);

        departureSelectablePorts.sort((portA, portB) =>
          portA.label.localeCompare(portB.label),
        );

        invalidPortIds.add(route.portOfDeparture.value);
      }

      const arrivalSelectablePorts = [...baseSelectablePorts];
      if (
        !arrivalSelectablePorts.find(
          port => port.value === route.portOfArrival.value,
        )
      ) {
        arrivalSelectablePorts.push(route.portOfArrival);

        arrivalSelectablePorts.sort((portA, portB) =>
          portA.label.localeCompare(portB.label),
        );
      }
      return {
        departureSelectablePorts,
        arrivalSelectablePorts,
        invalidPortIds,
      };
    }, [allowedPorts, route]);

  const initialValues = useMemo<RouteFormValues>(() => {
    if (!route) {
      return {
        departureTime: copiedRoute?.departureTime ?? null,
        dateFrom: copiedRoute?.dateFrom ?? null,
        dateTo: copiedRoute?.dateTo ?? null,
        portOfArrivalId:
          copiedRoute?.portOfArrival.value ?? defaultArrivalPort?.value ?? '',
        portOfDepartureId:
          copiedRoute?.portOfDeparture.value ??
          defaultDeparturePort?.value ??
          '',
        repeatedOnDays: copiedRoute?.repeatedOnDays ?? [],
      };
    }
    return {
      departureTime: route.departureTime,
      dateFrom: route.dateFrom,
      dateTo: route.dateTo,
      portOfArrivalId: route.portOfArrival.value,
      portOfDepartureId: route.portOfDeparture.value,
      repeatedOnDays: route.repeatedOnDays,
    };
  }, [
    copiedRoute,
    defaultArrivalPort?.value,
    defaultDeparturePort?.value,
    route,
  ]);

  const formState = useForm({
    initialValues,
    onSubmit: values => {
      onSave?.({
        dateFrom: values.dateFrom?.format(DAYJS_DATA_DATE_FORMAT) ?? '',
        dateTo: values.dateTo?.format(DAYJS_DATA_DATE_FORMAT) ?? '',
        departureTime: values.departureTime
          ? values.departureTime.format(DAYJS_DATA_TIME_FORMAT)
          : '',
        portOfArrivalId: values.portOfArrivalId,
        portOfDepartureId: values.portOfDepartureId,
        repeatOnDays: values.repeatedOnDays,
      });
    },
    validate: (values, setError) => {
      if (!values.portOfDepartureId) {
        setError('portOfDepartureId', t('validation.required-field'));
      } else if (invalidPortIds.has(values.portOfDepartureId)) {
        setError('portOfDepartureId', t('validation.invalid-option'));
      }
      if (!values.portOfArrivalId) {
        setError('portOfArrivalId', t('validation.required-field'));
      } else if (invalidPortIds.has(values.portOfArrivalId)) {
        setError('portOfArrivalId', t('validation.invalid-option'));
      } else if (values.portOfArrivalId === values.portOfDepartureId) {
        setError(
          'portOfArrivalId',
          t('validation.value-equal-to-another-field', {
            ANOTHER_FIELD: t('content.departure-port'),
          }),
        );
      }

      if (!values.dateFrom) {
        setError('dateFrom', t('validation.required-field'));
      } else if (!values.dateFrom.isValid()) {
        setError('dateFrom', t('validation.invalid-date'));
      } else if (
        contractDateFrom &&
        values.dateFrom.isBefore(contractDateFrom, 'day')
      ) {
        setError(
          'dateFrom',
          t('validation.date-before-reference-date', {
            REFERENCE_DATE: contractDateFrom.format(
              DAYJS_VISUALIZATION_DATE_FORMAT,
            ),
          }),
        );
      }

      if (!values.dateTo) {
        setError('dateTo', t('validation.required-field'));
      } else if (!values.dateTo.isValid()) {
        setError('dateTo', t('validation.invalid-date'));
      } else if (
        values.dateFrom &&
        (values.dateFrom.isSame(values.dateTo, 'day') ||
          values.dateFrom.isAfter(values.dateTo, 'day'))
      ) {
        setError('dateTo', t('validation.invalid-end-date'));
      } else if (
        contractDateTo &&
        values.dateTo.isAfter(contractDateTo, 'day')
      ) {
        setError(
          'dateTo',
          t('validation.date-after-reference-date', {
            REFERENCE_DATE: contractDateTo.format(
              DAYJS_VISUALIZATION_DATE_FORMAT,
            ),
          }),
        );
      }

      if (!values.repeatedOnDays.length) {
        setError('repeatedOnDays', t('validation.required-field'));
      }

      if (!values.departureTime) {
        setError('departureTime', t('validation.required-field'));
      } else if (!values.departureTime.isValid()) {
        setError('departureTime', t('validation.invalid-date'));
      }
    },
    differenceChecker: {
      repeatedOnDays: (a: RouteDay[], b: RouteDay[]) =>
        a.length !== b.length || (a.length > 0 && a.some(x => !b.includes(x))),
      dateFrom: getNullableDayjsDifferenceChecker('day'),
      dateTo: getNullableDayjsDifferenceChecker('day'),
      departureTime: getNullableDayjsDifferenceChecker('minute'),
    },
    resetOnInitialValuesChange: () => !!route,
  });

  const { values } = formState;

  const isConflictingWithAnotherRoute = useMemo(() => {
    if (
      existingRoutes.length == 0 ||
      !values.dateFrom ||
      !values.dateFrom.isValid() ||
      !values.dateTo ||
      !values.dateTo.isValid() ||
      !values.departureTime ||
      !values.departureTime.isValid() ||
      values.repeatedOnDays.length === 0
    ) {
      return false;
    }

    const currentRepeatedOnDays = new Set(values.repeatedOnDays);

    return existingRoutes.some(
      ({ id, dateFrom, dateTo, departureTime, repeatedOnDays }) =>
        id !== route?.id &&
        departureTime.isSame(values.departureTime, 'minute') &&
        repeatedOnDays.length > 0 &&
        repeatedOnDays.some(day => currentRepeatedOnDays.has(day)) &&
        (((values.dateFrom as Dayjs).isBefore(dateFrom) &&
          (values.dateTo as Dayjs).isAfter(dateTo)) ||
          [values.dateFrom as Dayjs, values.dateTo as Dayjs].some(
            date =>
              date.isSame(dateFrom, 'day') ||
              date.isSame(dateTo, 'day') ||
              (date.isAfter(dateFrom) && date.isBefore(dateTo)),
          )),
    );
  }, [route, values, existingRoutes]);

  const isDateFromInPastMonth =
    !route && values.dateFrom && values.dateFrom.isBefore(dayjs(), 'day');

  useNavigationGuardBlocker(() => formState.haveValuesChanged);

  const areInputsDisabled = isLoading || !onSave || Boolean(route);

  const { getBaseProps, getSelectProps, renderError } =
    getMaterialInputsFormHelper(formState, {
      shouldInputsBeDisabled: areInputsDisabled,
    });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card variant="outlined">
        <form
          onSubmit={formState.handleSubmit}
          className="p-6 grid grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-6"
        >
          <header className="col-span-full font-bold flex gap-4">
            <div className="flex gap-4">
              <span>{t('content.route')}</span>
              {!route && <CustomChip small label={t('content.draft')} />}
              {route?.routeStatus === RouteStatus.Disabled && (
                <CustomChip
                  className={BADGE_CLASSNAMES.RED}
                  small
                  label={t('route-status.disabled')}
                />
              )}
            </div>
            {!areActionsDisabled && (
              <div className="ml-auto flex gap-4">
                {route && onCopyToNextYear && (
                  <Tooltip
                    title={
                      canCopyToNextYear
                        ? t('content.copy-route-to-next-year-description')
                        : t(
                            'content.copy-route-to-next-year-exceeds-limits-description',
                          )
                    }
                  >
                    <span>
                      <IconButton
                        disabled={isLoading || !canCopyToNextYear}
                        onClick={() =>
                          /* TODO  send data for next year */ onCopyToNextYear()
                        }
                      >
                        <SvgIcon component={RiCornerUpRightDoubleLine} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <div className="ml-auto flex gap-4">
                  {route && route.routeStatus !== RouteStatus.Disabled && (
                    <Tooltip title={t('content.rescind-contract-route')}>
                      <span>
                        <IconButton
                          disabled={isLoading}
                          onClick={() => onRescind?.()}
                          className="ml-auto"
                        >
                          <SvgIcon component={RiForbid2Line} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={t('content.delete-contract-route')}>
                    <span>
                      <IconButton
                        disabled={isLoading}
                        onClick={() => onDelete(formState.haveValuesChanged)}
                        className="ml-auto"
                      >
                        <SvgIcon component={RiDeleteBinLine} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}
          </header>
          <div className="col-span-3 grid grid-cols-[1fr_min-content_1fr] grid-rows-[repeat(2,auto)] grid-flow-col-dense">
            <label className="w-full row-span-2 grid grid-rows-subgrid">
              <InputHeader required>{t('content.departure-port')}</InputHeader>
              <FormControl error={getBaseProps('portOfDepartureId').error}>
                <Select
                  {...getSelectProps('portOfDepartureId')}
                  displayEmpty
                  renderValue={id => {
                    const notSelectedElement = (
                      <span className="text-iots-grey-400">Select...</span>
                    );
                    if (!id) {
                      return notSelectedElement;
                    }
                    const portSelectItem = departureSelectablePorts.find(
                      port => port.value === id,
                    );
                    if (!portSelectItem) {
                      return notSelectedElement;
                    }

                    return renderPortSelectItemLabel(portSelectItem, t);
                  }}
                >
                  {departureSelectablePorts.map(portSelectItem => (
                    <MenuItem
                      key={portSelectItem.value}
                      value={portSelectItem.value}
                    >
                      {renderPortSelectItemLabel(portSelectItem, t)}
                    </MenuItem>
                  ))}
                </Select>
                {renderError('portOfDepartureId')}
              </FormControl>
            </label>
            <IconButton
              className="row-start-2 self-start"
              disabled={areInputsDisabled}
              onClick={() => {
                formState.updateValues({
                  portOfDepartureId: values.portOfArrivalId,
                  portOfArrivalId: values.portOfDepartureId,
                });
              }}
            >
              <SvgIcon component={RiArrowLeftRightLine} />
            </IconButton>
            <label className="w-full row-span-2 grid grid-rows-subgrid">
              <InputHeader required>{t('content.arrival-port')}</InputHeader>
              <FormControl error={getBaseProps('portOfArrivalId').error}>
                <Select
                  {...getSelectProps('portOfArrivalId')}
                  displayEmpty
                  renderValue={id => {
                    const notSelectedElement = (
                      <span className="text-iots-grey-400">Select...</span>
                    );
                    if (!id) {
                      return notSelectedElement;
                    }
                    const portSelectItem = arrivalSelectablePorts.find(
                      port => port.value === id,
                    );
                    if (!portSelectItem) {
                      return notSelectedElement;
                    }

                    return renderPortSelectItemLabel(portSelectItem, t);
                  }}
                >
                  {arrivalSelectablePorts.map(portSelectItem => (
                    <MenuItem
                      key={portSelectItem.value}
                      value={portSelectItem.value}
                    >
                      {renderPortSelectItemLabel(portSelectItem, t)}
                    </MenuItem>
                  ))}
                </Select>
                {renderError('portOfArrivalId')}
              </FormControl>
            </label>
          </div>
          <div className="col-span-3 flex gap-2">
            <label className="block w-full">
              <InputHeader required>{t('content.start-date')}</InputHeader>
              <FormControl error={getBaseProps('dateFrom').error}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: getBaseProps('dateFrom').error,
                      onBlur: getBaseProps('dateFrom').onBlur,
                      disabled: getBaseProps('dateFrom').disabled,
                    },
                  }}
                  value={values.dateFrom}
                  minDate={contractDateFrom ?? undefined}
                  maxDate={values.dateTo ?? contractDateTo ?? undefined}
                  onChange={newValue =>
                    formState.updateValues({
                      dateFrom: newValue,
                    })
                  }
                  disabled={getBaseProps('dateFrom').disabled}
                />
                {renderError('dateFrom')}
              </FormControl>
            </label>
            <label className="block w-full">
              <InputHeader required>{t('content.end-date')}</InputHeader>
              <FormControl error={getBaseProps('dateTo').error}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: getBaseProps('dateTo').error,
                      onBlur: getBaseProps('dateTo').onBlur,
                      disabled: getBaseProps('dateTo').disabled,
                    },
                  }}
                  value={values.dateTo}
                  minDate={values.dateFrom ?? contractDateFrom ?? undefined}
                  maxDate={contractDateTo ?? undefined}
                  onChange={newValue =>
                    formState.updateValues({
                      dateTo: newValue,
                    })
                  }
                  disabled={getBaseProps('dateTo').disabled}
                />
                {renderError('dateTo')}
              </FormControl>
            </label>
          </div>
          <div className="col-span-3 lg:col-span-4">
            <InputHeader required>{t('content.repeats-on')}</InputHeader>
            <FormControl error={getBaseProps('repeatedOnDays').error}>
              <ToggleButtonGroup
                value={values.repeatedOnDays}
                onChange={(_event, values) => {
                  formState.updateValues(
                    {
                      repeatedOnDays: values as unknown as RouteDay[],
                    },
                    true,
                  );
                }}
                disabled={getBaseProps('repeatedOnDays').disabled}
              >
                {Object.values(RouteDay).map(day => (
                  <ToggleButton key={day} value={day}>
                    {t(`weekdays.${day}.compact`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {renderError('repeatedOnDays')}
            </FormControl>
          </div>
          <div className="col-span-3 lg:col-span-2">
            <label className="block w-full">
              <InputHeader required>{t('content.departure-time')}</InputHeader>
              <FormControl error={getBaseProps('departureTime').error}>
                <TimePicker
                  ampm={false}
                  className="w-full"
                  slotProps={{
                    textField: {
                      slotProps: {
                        input: {
                          error: getBaseProps('departureTime').error,
                          onBlur: getBaseProps('departureTime').onBlur,
                          disabled: getBaseProps('departureTime').disabled,
                        },
                      },
                    },
                  }}
                  value={values.departureTime}
                  onChange={newValue =>
                    formState.updateValues({ departureTime: newValue })
                  }
                  disabled={getBaseProps('departureTime').disabled}
                />
                {renderError('departureTime')}
              </FormControl>
            </label>
          </div>
          {isConflictingWithAnotherRoute && (
            <div className="col-span-full">
              <Alert severity={route ? 'info' : 'warning'}>
                <AlertTitle>{t('content.overlapping-route')}</AlertTitle>
                {t(
                  route
                    ? 'content.overlapping-route-already-exists-info'
                    : 'content.overlapping-route-already-exists-warning',
                )}
              </Alert>
            </div>
          )}
          {isDateFromInPastMonth && (
            <div className="col-span-full">
              <Alert severity={'warning'}>
                <AlertTitle>
                  {t('content.route-starts-in-a-past-month')}
                </AlertTitle>
                {t('content.route-starts-in-a-past-month-warning')}
              </Alert>
            </div>
          )}
          {onSave && (
            <div className="col-span-full flex justify-end">
              <Button
                variant="contained"
                type="submit"
                disabled={
                  ((route || !copiedRoute) && !formState.haveValuesChanged) ||
                  areInputsDisabled ||
                  formState.hasVisibleErrors
                }
              >
                {t('content.save')}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </LocalizationProvider>
  );
};

export default RouteElement;
